import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Customer, PurchaseOrder, Project } from '../types';
import { customerAPI, purchaseOrderAPI, projectAPI, purchaseOrderDocumentAPI } from '../services/api';
import { PAYMENT_TERMS } from '../constants/paymentTerms';
import DocumentUpload from './DocumentUpload';
import DocumentList from './DocumentList';

const PurchaseOrderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [formData, setFormData] = useState<PurchaseOrder>({
    po_number: '',
    customer: 0,
    projects: [],
    issue_date: new Date().toISOString().split('T')[0],
    payment_terms: 'net_30',
    custom_days: undefined,
    status: 'draft',
    description: '',
    notes: '',
    total_value: '',
    start_date: '',
    end_date: '',
  });
  const [currentPO, setCurrentPO] = useState<PurchaseOrder | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchProjects();
    if (isEditMode && id) {
      fetchPurchaseOrder(parseInt(id));
    } else {
      // Pre-select project if coming from project view
      const projectParam = searchParams.get('project');
      if (projectParam) {
        setFormData((prev) => ({ ...prev, projects: [parseInt(projectParam)] }));
      }
    }
  }, [id, isEditMode, searchParams]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data.results);
    } catch (err) {
      console.error('Failed to fetch customers', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data.results.filter(p => p.is_active));
    } catch (err) {
      console.error('Failed to fetch projects', err);
    }
  };

  const fetchPurchaseOrder = async (poId: number) => {
    try {
      setLoading(true);
      const response = await purchaseOrderAPI.getById(poId);
      setFormData(response.data);
      setCurrentPO(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch purchase order');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentsUpdate = () => {
    if (id) {
      fetchPurchaseOrder(parseInt(id));
    }
  };

  const handleAddPendingFile = (file: File) => {
    setPendingFiles((prev) => [...prev, file]);
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'customer' || name === 'custom_days' ? (parseFloat(value) || 0) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.payment_terms === 'custom' && !formData.custom_days) {
      setError('Custom days is required when payment terms is "custom"');
      return;
    }
    
    // Validate Period of Performance dates
    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) >= new Date(formData.end_date)) {
        setError('Period of Performance end date must be after start date');
        return;
      }
    }
    
    try {
      setLoading(true);
      let createdPoId: number | undefined;
      
      if (isEditMode && id) {
        await purchaseOrderAPI.update(parseInt(id), formData);
      } else {
        const response = await purchaseOrderAPI.create(formData);
        createdPoId = response.data.id;
        
        // Upload pending files if any
        if (pendingFiles.length > 0 && createdPoId) {
          setUploadingDocuments(true);
          setError('Purchase Order created. Uploading documents...');
          
          for (const file of pendingFiles) {
            try {
              await purchaseOrderDocumentAPI.upload(createdPoId, file);
            } catch (uploadErr) {
              console.error('Failed to upload file:', file.name, uploadErr);
              // Continue uploading other files even if one fails
            }
          }
          
          setUploadingDocuments(false);
        }
      }
      navigate('/purchase-orders');
    } catch (err) {
      setError('Failed to save purchase order');
      console.error(err);
    } finally {
      setLoading(false);
      setUploadingDocuments(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>{isEditMode ? 'Edit Purchase Order' : 'Create New Purchase Order'}</h2>
        
        {error && <div className="error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="po_number">PO Number *</label>
            <input
              type="text"
              id="po_number"
              name="po_number"
              value={formData.po_number}
              onChange={handleChange}
              placeholder="PO-2024-001"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="customer">Company *</label>
            <select
              id="customer"
              name="customer"
              value={formData.customer}
              onChange={handleChange}
              required
            >
              <option value="">Select a company</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="projects">Projects (Optional)</label>
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '0.75rem',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {projects.length === 0 ? (
                <p style={{ color: '#7f8c8d', margin: 0 }}>No active projects available</p>
              ) : (
                projects.map((project) => (
                  <label 
                    key={project.id}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={formData.projects?.includes(project.id!) || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          projects: checked
                            ? [...(prev.projects || []), project.id!]
                            : (prev.projects || []).filter((id) => id !== project.id)
                        }));
                      }}
                      style={{ 
                        cursor: 'pointer',
                        flexShrink: 0,
                        width: '18px',
                        height: '18px'
                      }}
                    />
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: '#3498db',
                      flexShrink: 0,
                      minWidth: '80px'
                    }}>
                      {project.code}
                    </span>
                    <span style={{ 
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {project.name}
                    </span>
                    {project.description && (
                      <span style={{ 
                        fontSize: '0.85rem',
                        color: '#95a5a6',
                        fontStyle: 'italic',
                        flexShrink: 0,
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {project.description}
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>
            <small style={{ color: '#7f8c8d', marginTop: '0.5rem', display: 'block' }}>
              Link this PO to one or more projects. Multiple projects can be selected.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="issue_date">Issue Date *</label>
            <input
              type="date"
              id="issue_date"
              name="issue_date"
              value={formData.issue_date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="total_value">Total Value</label>
            <input
              type="number"
              id="total_value"
              name="total_value"
              value={formData.total_value || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
            <small style={{ color: '#7f8c8d', marginTop: '0.5rem', display: 'block' }}>
              Total value of the purchase order in dollars
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="start_date">Period of Performance - Start Date</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formData.start_date || ''}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="end_date">Period of Performance - End Date</label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={formData.end_date || ''}
              onChange={handleChange}
            />
            {formData.start_date && formData.end_date && (
              <small style={{ color: '#7f8c8d', marginTop: '0.5rem', display: 'block' }}>
                Duration: {Math.ceil((new Date(formData.end_date).getTime() - new Date(formData.start_date).getTime()) / (1000 * 60 * 60 * 24))} days
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="payment_terms">Payment Terms *</label>
            <select
              id="payment_terms"
              name="payment_terms"
              value={formData.payment_terms}
              onChange={handleChange}
              required
            >
              {PAYMENT_TERMS.map((term) => (
                <option key={term.value} value={term.value}>
                  {term.label}
                </option>
              ))}
            </select>
          </div>

          {formData.payment_terms === 'custom' && (
            <div className="form-group">
              <label htmlFor="custom_days">Custom Days *</label>
              <input
                type="number"
                id="custom_days"
                name="custom_days"
                value={formData.custom_days || ''}
                onChange={handleChange}
                min="0"
                placeholder="Enter number of days"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Brief description of this purchase order"
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Additional notes"
            />
          </div>

          {/* Documents Section */}
          {!isEditMode && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e9ecef' }}>
              <h4 style={{ marginBottom: '1rem' }}>Documents (Optional)</h4>
              <PendingDocumentUpload
                pendingFiles={pendingFiles}
                onAddFile={handleAddPendingFile}
                onRemoveFile={handleRemovePendingFile}
              />
            </div>
          )}

          <div className="button-group">
            <button type="submit" className="btn btn-primary" disabled={loading || uploadingDocuments}>
              {uploadingDocuments ? 'Uploading Documents...' : loading ? 'Saving...' : isEditMode ? 'Update Purchase Order' : 'Create Purchase Order'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/purchase-orders')}
              disabled={loading || uploadingDocuments}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Documents Section for Edit Mode */}
        {isEditMode && currentPO && (
          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #e9ecef' }}>
            <h3 style={{ marginBottom: '1rem' }}>Documents</h3>
            <DocumentList
              documents={currentPO.documents || []}
              onDelete={handleDocumentsUpdate}
            />
            <DocumentUpload
              purchaseOrderId={currentPO.id!}
              onUploadComplete={handleDocumentsUpdate}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Component for handling pending file uploads in create mode
interface PendingDocumentUploadProps {
  pendingFiles: File[];
  onAddFile: (file: File) => void;
  onRemoveFile: (index: number) => void;
}

const PendingDocumentUpload: React.FC<PendingDocumentUploadProps> = ({ pendingFiles, onAddFile, onRemoveFile }) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  const maxSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Allowed: PDF, Images (JPEG, PNG, GIF), Word documents';
    }
    if (file.size > maxSize) {
      return 'File size exceeds 10MB limit';
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      alert(validationError);
      return;
    }
    onAddFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => handleFileSelect(file));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files) {
      Array.from(files).forEach(file => handleFileSelect(file));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (type: string): string => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼️';
    if (type.includes('word') || type.includes('document')) return '📝';
    return '📎';
  };

  return (
    <div>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#3498db' : '#ddd'}`,
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: dragOver ? '#f0f8ff' : '#fafafa',
          transition: 'all 0.3s ease',
          marginBottom: '1rem'
        }}
      >
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📁</div>
        <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>
          Click to browse or drag and drop files here
        </p>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#7f8c8d' }}>
          Supported: PDF, Images, Word documents (Max 10MB each)
        </p>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#95a5a6' }}>
          Files will be uploaded after the PO is created
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
        multiple
        style={{ display: 'none' }}
      />

      {/* List of pending files */}
      {pendingFiles.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>
            Files to upload ({pendingFiles.length})
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {pendingFiles.map((file, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                  <span style={{ fontSize: '2rem' }}>{getFileIcon(file.type)}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      {file.name}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveFile(index)}
                  className="btn btn-danger btn-small"
                  title="Remove"
                >
                  🗑️ Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderForm;
